import React, { useState, useEffect } from 'react';
import { version } from '../../package.json';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Lock, User, Eye, EyeOff, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';
import { useAcademyStore } from '../../store/academyStore';
import { useMusicStore } from '../../store/musicStore';
import { useSfx } from '../../store/sfxStore';

type AuthMode = 'login' | 'signup';

const AVATARS = [
    'gorilla_working', 'gorilla_chef', 'gorilla_artist', 'gorilla_astronaut',
    'gorilla_backpacker', 'gorilla_city_planner', 'gorilla_robot', 'gorilla_security'
];

export const AuthScreen = () => {
    const [mode, setMode] = useState<AuthMode>('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');
    const [selectedAvatar, setSelectedAvatar] = useState(AVATARS[0]);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { loginUser } = useAcademyStore();
    const { playTrack, setVolume } = useMusicStore();
    const { play } = useSfx();

    useEffect(() => {
        setVolume(0.1);
        playTrack('menu');
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            // Check if Supabase is configured
            if (!isSupabaseConfigured()) {
                // Offline mode - use local storage only
                console.log('Running in offline mode');
                loginUser({
                    id: `local_${Date.now()}`,
                    username: username || email.split('@')[0],
                    avatar: selectedAvatar,
                });
                return;
            }

            if (mode === 'signup') {
                const { data, error: signUpError } = await supabase.auth.signUp({
                    email,
                    password,
                    options: {
                        data: {
                            username: username,
                            avatar: selectedAvatar,
                        }
                    }
                });

                if (signUpError) throw signUpError;

                if (data.user) {
                    loginUser({
                        id: data.user.id,
                        username: username,
                        avatar: selectedAvatar,
                    });
                }
            } else {
                const { data, error: signInError } = await supabase.auth.signInWithPassword({
                    email,
                    password,
                });

                if (signInError) throw signInError;

                if (data.user) {
                    const metadata = data.user.user_metadata;
                    loginUser({
                        id: data.user.id,
                        username: metadata?.username || email.split('@')[0],
                        avatar: metadata?.avatar || 'gorilla_working',
                    });
                }
            }
        } catch (err: any) {
            console.error('Auth error:', err);
            setError(err.message || 'Authentication failed');
            play('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-green-900 via-emerald-800 to-teal-900 flex items-center justify-center p-4 relative overflow-hidden">
            {/* Animated Background */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-20 left-20 w-72 h-72 bg-green-500/20 rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
                <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-teal-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="relative z-10 w-full max-w-md"
            >
                {/* Logo & Title */}
                <div className="text-center mb-8">
                    <motion.img
                        src="/assets/branding/logo.png"
                        alt="Gorilla Scouts"
                        className="h-24 mx-auto mb-4 drop-shadow-2xl"
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    />
                    <h1 className="text-4xl font-black text-white font-comic tracking-tight drop-shadow-lg">
                        Gorilla Scouts
                    </h1>
                    <p className="text-green-200 mt-2 font-medium">
                        {mode === 'login' ? 'Welcome back, Scout!' : 'Join the troop!'}
                    </p>
                </div>

                {/* Auth Card */}
                <motion.div
                    className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border-4 border-white/50"
                    layout
                >
                    {/* Mode Toggle */}
                    <div className="flex bg-slate-100 rounded-xl p-1 mb-6">
                        {(['login', 'signup'] as AuthMode[]).map((m) => (
                            <button
                                key={m}
                                onClick={() => { setMode(m); setError(null); play('click'); }}
                                onMouseEnter={() => play('hover')}
                                className={`flex-1 py-3 rounded-lg font-bold text-sm uppercase tracking-wider transition-all ${mode === m
                                    ? 'bg-green-600 text-white shadow-lg'
                                    : 'text-slate-500 hover:text-slate-700'
                                    }`}
                            >
                                {m === 'login' ? 'Sign In' : 'Sign Up'}
                            </button>
                        ))}
                    </div>

                    {/* Error Message */}
                    <AnimatePresence>
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-4 flex items-start gap-3"
                            >
                                <AlertCircle className="text-red-500 shrink-0 mt-0.5" size={20} />
                                <p className="text-red-700 text-sm font-medium">{error}</p>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Username (Signup Only) */}
                        <AnimatePresence>
                            {mode === 'signup' && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                >
                                    <label className="block text-sm font-bold text-slate-600 mb-2">
                                        Scout Name
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                        <input
                                            type="text"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            placeholder="Your jungle name"
                                            className="w-full pl-12 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
                                            required={mode === 'signup'}
                                        />
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Email */}
                        <div>
                            <label className="block text-sm font-bold text-slate-600 mb-2">
                                Email
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="scout@jungle.com"
                                    className="w-full pl-12 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
                                    required
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div>
                            <label className="block text-sm font-bold text-slate-600 mb-2">
                                Password
                            </label>
                            <div className="relative">
                                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full pl-12 pr-12 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl font-medium focus:border-green-500 focus:ring-4 focus:ring-green-100 outline-none transition-all"
                                    required
                                    minLength={6}
                                />
                                <button
                                    type="button"
                                    onClick={() => { setShowPassword(!showPassword); play('click'); }}
                                    onMouseEnter={() => play('hover')}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                                >
                                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                                </button>
                            </div>
                        </div>

                        {/* Avatar Selection (Signup Only) */}
                        <AnimatePresence>
                            {mode === 'signup' && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                >
                                    <label className="block text-sm font-bold text-slate-600 mb-3">
                                        Choose Your Avatar
                                    </label>
                                    <div className="grid grid-cols-4 gap-3">
                                        {AVATARS.map((avatar) => (
                                            <motion.button
                                                key={avatar}
                                                type="button"
                                                onClick={() => { setSelectedAvatar(avatar); play('click'); }}
                                                onMouseEnter={() => play('hover')}
                                                className={`aspect-square rounded-xl p-2 border-4 transition-all ${selectedAvatar === avatar
                                                    ? 'border-green-500 bg-green-50 shadow-lg scale-105'
                                                    : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                                                    }`}
                                                whileHover={{ scale: 1.05 }}
                                                whileTap={{ scale: 0.95 }}
                                            >
                                                <img
                                                    src={`/assets/gorillas/${avatar}.png`}
                                                    alt={avatar}
                                                    className="w-full h-full object-contain"
                                                />
                                            </motion.button>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Submit Button */}
                        <motion.button
                            type="submit"
                            disabled={loading}
                            onClick={() => play('click')}
                            onMouseEnter={() => play('hover')}
                            className="w-full py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-black text-lg rounded-xl shadow-lg hover:shadow-green-300/50 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                            whileHover={{ scale: loading ? 1 : 1.02 }}
                            whileTap={{ scale: loading ? 1 : 0.98 }}
                        >
                            {loading ? (
                                <Loader2 className="animate-spin" size={24} />
                            ) : (
                                <>
                                    <Sparkles size={20} />
                                    {mode === 'login' ? 'Enter the Jungle' : 'Join the Scouts'}
                                </>
                            )}
                        </motion.button>
                    </form>

                    {/* Offline Mode Notice */}
                    {!isSupabaseConfigured() && (
                        <div className="mt-4 text-center text-xs text-slate-400 bg-slate-50 rounded-lg p-3">
                            🔌 Running in offline mode. Progress saved locally.
                        </div>
                    )}
                </motion.div>

                {/* Footer */}
                <p className="text-center text-green-200/60 text-sm mt-6">
                    © 2026 Grounded Growth • Code Stronger Together
                </p>
                <p className="text-center text-green-200/30 text-xs mt-2 font-mono">
                    v{version}
                </p>
            </motion.div>
        </div>
    );
};
